import type { BoxProps } from "@mantine/core";
import { Checkbox, Group, NumberInput, Select, Text, Textarea, TextInput, Tooltip } from "@mantine/core";
import { DateInput } from "@mantine/dates";

import type { FieldIndentifier } from "@packages/lib";
import { IconEdit, IconLock } from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useConfigStore } from "~/dexie/store";

type FieldType =
  | "text"
  | "textarea"
  | "numeric"
  | "integer"
  | "checkbox"
  | "switch"
  | "select"
  | "multi-select"
  | "percentage"
  | "date"
  | "time"
  | "datetime"

export type CrmFieldProps = {
  field: FieldIndentifier;
  value?: any;
  onChange?: (value: any) => void;
  /** Optionally override the field type determined by the config */
  as?: FieldType;
  /** When provided, indicates that the field has unsaved changes */
  isDirty?: boolean;
  /** Forces the field to be read-only. Default is `false` 
  * When it's set to `true`, The tooptip shows default message
  * When it's a string, The tooptip shows the provided message  
  */
  readOnly?: boolean | string;
  withAsterisk?: boolean;
  [key: string]: any;
} & BoxProps;

export function CrmField({ field, value, onChange, as: asField, readOnly, isDirty, ...otherProps }: CrmFieldProps) {
  const [inputValue, setInputValue] = useState(value);
  const getConfigByField = useConfigStore((s) => s.getConfigByField);
  useConfigStore((s) => s.isReady); // subscribe so we re-render when store finishes loading
  const config = getConfigByField(field);

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const isEditable = (config?.isEditable ?? false) && !(readOnly ?? false);
  const labelText = config?.label || field.split(".").at(-1);

  const fieldType = useMemo((): FieldType => {
    if (asField) return asField;
    if (!config) return "text";
    const customType = config?.customType;
    const columnType = config.columnType;

    if (customType === "select") return "select";
    if (columnType === "boolean") return "checkbox";
    if (columnType === "numeric") return "numeric";
    if (columnType === "date") return "date";
    if (columnType === "time without time zone") return "time";

    return "text";
  }, [config, asField]);

  const label = (
    <Group gap="xs">
      <Text fw={500} fz="sm">
        {labelText}
      </Text>
      {isDirty && <IconEdit size={16} color="orange" />}
      {!isEditable && <Tooltip label={typeof readOnly === "string" ? readOnly : "This field is not editable"} position="right">
        <IconLock size={14} color="gray" />
      </Tooltip>}
    </Group>
  )

  const handleChange = useCallback(
    (value: any) => {
      const type = typeof value;

      // Handle special cases
      if (fieldType === "numeric") {
        const newValue = type === "number" ? value.toFixed(2) : value === "" || value == null ? null : value;
        if (newValue === inputValue) return;
        setInputValue(newValue);
        onChangeRef.current?.(newValue);
        return;
      }

      if (type === "object" && (value instanceof Event || value?.nativeEvent instanceof Event)) {
        const target = value.target as HTMLInputElement | undefined;
        let newValue: any;

        if (fieldType === "checkbox") {
          newValue = target?.checked ?? null;
        } else {
          newValue = target?.value;
        }

        setInputValue(newValue);
        onChangeRef.current?.(newValue);
        return;
      }

      // Default case
      setInputValue(value);
      onChangeRef.current?.(value);
    },
    [fieldType, inputValue],
  );


  if (fieldType === "select") {
    return (
      <Select
        label={label}
        readOnly={!isEditable}
        data={config?.options}
        value={inputValue}
        onChange={handleChange}
        {...otherProps}
      />
    );
  }

  if (fieldType === "checkbox") {
    return (
      <Checkbox
        label={label}
        readOnly={!isEditable}
        checked={!!inputValue}
        indeterminate={inputValue === null}
        onChange={handleChange}
        {...otherProps}
      />
    );
  }

  if (fieldType === "numeric") {
    return (
      <NumberInput
        label={label}
        readOnly={!isEditable}
        decimalScale={2}
        thousandSeparator=" "
        decimalSeparator=","
        value={inputValue ?? ""}
        hideControls
        onChange={handleChange}
        {...otherProps}
      />
    );
  }

  if (fieldType === "textarea") {
    return (
      <Textarea
        label={label}
        readOnly={!isEditable}
        value={inputValue ?? ""}
        onChange={handleChange}
        autosize
        {...otherProps}
      />
    );
  }

  if (fieldType === "date") {
    return (
      <DateInput
        label={label}
        readOnly={!isEditable}
        value={inputValue}
        onChange={handleChange}
        {...otherProps}
      />
    );
  }

  return (
    <TextInput
      label={label}
      readOnly={!isEditable}
      value={inputValue ?? ""}
      onChange={handleChange}
      {...otherProps}
    />
  );
}
